import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

/** Format number in Indian numbering system. e.g. 267661 -> "2,67,661" */
export function formatIndian(num, options = {}) {
  if (num === null || num === undefined || isNaN(num)) return "—";
  const { decimals = 0 } = options;
  const negative = num < 0;
  const abs = Math.abs(Number(num));
  const fixed = abs.toFixed(decimals);
  const [intPart, decPart] = fixed.split(".");
  let result;
  if (intPart.length <= 3) {
    result = intPart;
  } else {
    const last3 = intPart.slice(-3);
    const rest = intPart.slice(0, -3);
    const withCommas = rest.replace(/\B(?=(\d{2})+(?!\d))/g, ",");
    result = withCommas + "," + last3;
  }
  return (negative ? "-" : "") + result + (decPart ? "." + decPart : "");
}

export function rupees(num, options = {}) {
  if (num === null || num === undefined || isNaN(num)) return "—";
  return "₹" + formatIndian(num, options);
}

/**
 * CS 21.4 — Single source of truth for cost/sell money formatting in the
 * Wholesaler Portal. Used everywhere a piece-level money figure is shown.
 *
 *   USD → "$1,234.56"   (en-US grouping, 2-decimal)
 *   INR → "₹1,23,456"   (Indian grouping, whole rupees; rounding is
 *                        applied upstream by the selling engine to nearest
 *                        ₹100 by default)
 *
 * @param {number|string|null} value
 * @param {"USD"|"INR"} currency
 * @param {{ decimals?: number }} options
 */
export function formatMoney(value, currency = "USD", options = {}) {
  if (value === null || value === undefined || value === "" || isNaN(Number(value))) return "—";
  const n = Number(value);
  const cur = String(currency || "USD").toUpperCase();
  if (cur === "INR") {
    const decimals = options.decimals ?? 0;
    return "₹" + formatIndian(n, { decimals });
  }
  // CS 21.85 (APS v7) — GBP wholesalers display pounds with en-GB grouping
  // and 2-decimal precision, matching USD rules (user: "A known GBP test
  // amount must stay numerically identical on save/reload and display with £").
  // CS 22.7 — EUR renders exactly like GBP, only the symbol differs.
  if (cur === "GBP" || cur === "EUR") {
    const decimals = options.decimals ?? 2;
    const sign = n < 0 ? "-" : "";
    return sign + (cur === "EUR" ? "€" : "£") + Math.abs(n).toLocaleString("en-GB", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  }
  // USD (default)
  const decimals = options.decimals ?? 2;
  const sign = n < 0 ? "-" : "";
  return sign + "$" + Math.abs(n).toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/** Currency symbol only — for input adornments. */
export function currencySymbol(currency = "USD") {
  return { INR: "₹", USD: "$", GBP: "£", EUR: "€" }[String(currency || "USD").toUpperCase()] || "$";
}

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function purityColor(purity) {
  const p = (purity || "").toUpperCase();
  if (p === "18KT") return "bg-[#C9A84C]/15 text-[#8a6f1b] border-[#C9A84C]/30";
  if (p === "14KT") return "bg-[#D97706]/12 text-[#9a4a05] border-[#D97706]/30";
  if (p === "9KT") return "bg-slate-300/40 text-slate-700 border-slate-400/40";
  if (p === "PT950") return "bg-slate-700/10 text-slate-700 border-slate-700/30";
  if (p === "925") return "bg-slate-200/60 text-slate-600 border-slate-400/40";
  return "bg-slate-100 text-slate-600 border-slate-300";
}

// CS 22.12 -- canonical net gold weight: 2 decimals, half-up (mirrors backend weights.canonical_weight).
export function canonicalWeight(w) {
  const n = Number(w);
  if (!Number.isFinite(n)) return 0;
  const [i, f = ""] = Math.abs(n).toPrecision(15).split(".");
  const d = (f + "000").slice(0, 3);
  const cents = Number(i) * 100 + Number(d.slice(0, 2)) + (d[2] >= "5" ? 1 : 0);
  return (n < 0 ? -cents : cents) / 100;
}

export function formatWeight(w) {
  if (w === null || w === undefined || w === "" || isNaN(Number(w))) return "—";
  return formatIndian(canonicalWeight(w), { decimals: 2 });
}
